import React, { useEffect, useState, useRef, createElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, MapPin, Users, Calendar, FileText, Download, MessageSquare, ExternalLink, Tv, FileType, File, HeartHandshake, ChevronDown, ChevronUp, Tag, Play, Radio, CheckCircle } from 'lucide-react';
import type { ScheduleSession, Attachment } from '../../types/schedule.types';
import { generateCalendarUrl, extractSessionDate } from '../../utils/calendarUtils';
import { groupSpeakersByType, getSpeakerTypeLabel } from '../../../utils/speakerUtils';
import { CategoryBadge } from './CategoryBadge';
import { LoadingPlaceholder } from '../common/LoadingPlaceholder';
import { useLivestreamViewer } from '../../public_event/hooks/useLivestreamViewer';
import { livestreamTracking } from '../../event_dashboard/services/livestreamTrackingService';

interface SessionModalProps {
  session: ScheduleSession | null;
  onClose: () => void;
  timeZoneLabel?: string;
  onCalendarAdd?: (type: 'google' | 'outlook' | 'apple' | 'outlookcom' | 'office365' | 'yahoo') => void;
  // Modal wrapper component - will be provided by the parent
  ModalWrapper: React.ComponentType<{ isOpen: boolean; onClose: () => void; children: React.ReactNode }>;
  // Optional speaker click handler and speaker modal component
  onSpeakerClick?: (speakerId: string) => void;
  SpeakerModal?: React.ComponentType<{ speaker: any; onClose: () => void }>;
  speakers?: any[]; // Full speaker data if available
  primaryColor?: string; // Event's primary color
  eventDetails?: any; // Event details for calendar icons
  eventSlug?: string; // Optional event slug - if not provided, will try to get from EventContext
  eventDate?: string; // The actual date of the session (from the day)
  initialTab?: 'details' | 'attachments'; // Initial tab to show
  isLoading?: boolean; // Loading state for session data
  error?: Error | null; // Error state for session data
}

export const SessionModal = ({
  session,
  onClose,
  timeZoneLabel,
  onCalendarAdd,
  ModalWrapper,
  onSpeakerClick,
  SpeakerModal,
  speakers: allSpeakers,
  primaryColor = '#2563eb',
  eventDetails,
  eventSlug,
  eventDate,
  initialTab = 'details',
  isLoading = false,
  error = null
}: SessionModalProps) => {
  // Handle loading and error states early
  if (isLoading || !session) {
    return (
      <ModalWrapper isOpen={true} onClose={onClose}>
        <div className="p-6">
          {isLoading ? (
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Loading Session Details</h2>
                <LoadingPlaceholder type="form" />
              </div>
            </div>
          ) : error ? (
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Error Loading Session</h2>
              <p className="text-gray-600 mb-4">
                {error.message || 'Unable to load session details. Please try again.'}
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Session Not Found</h2>
              <p className="text-gray-600 mb-4">The requested session could not be found.</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </ModalWrapper>
    );
  }

  const {
    title,
    description,
    speakers,
    time,
    endTime,
    room,
    type,
    capacity,
    registeredCount,
    image,
    attachments,
    survey,
    isLivestream,
    livestreamId,
    livestreamUrl,
    sponsors
  } = session;
  const [activeTab, setActiveTab] = useState<'details' | 'attachments'>(initialTab);
  const [showCalendarDropdown, setShowCalendarDropdown] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState<any | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Use event slug from props or context
  const navigate = useNavigate();
  const { data: livestreamData, isLoading: livestreamLoading } = useLivestreamViewer(eventSlug, livestreamId);

  // Set event slug for tracking when component mounts
  useEffect(() => {
    if (eventSlug) {
      livestreamTracking.setEventSlug(eventSlug);
    }
  }, [eventSlug]);

  // Helper function to check if session is currently live based on time
  const isSessionCurrentlyLive = () => {
    if (!session.time || !session.endTime) return false;

    // For Link type livestreams, check session times
    // For Embed/Interactive types, rely on API status
    if (livestreamData?.streamType !== 'Link') {
      return livestreamData?.isLive || false;
    }

    // Parse session times (format: "9:00 AM" or "2:30 PM")
    const parseSessionTime = (timeStr: string): Date => {
      const now = new Date();
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);

      let hour24 = hours;
      if (period === 'PM' && hours !== 12) hour24 += 12;
      if (period === 'AM' && hours === 12) hour24 = 0;

      const sessionDate = new Date(now);
      sessionDate.setHours(hour24, minutes, 0, 0);
      return sessionDate;
    };

    try {
      const now = new Date();
      const startTime = parseSessionTime(session.time);
      const endTime = parseSessionTime(session.endTime);

      // Handle case where end time is past midnight
      if (endTime < startTime) {
        endTime.setDate(endTime.getDate() + 1);
      }

      return now >= startTime && now <= endTime;
    } catch {
      return false;
    }
  };

  // Helper to determine if a Link-type session livestream is in the past
  const isLinkSessionPast = () => {
    if (livestreamData?.streamType !== 'Link' || !session.endTime) {
      return false;
    }

    // For Link-type sessions, check if session end time has passed
    try {
      const now = new Date();
      const [time, period] = session.endTime.split(' ');
      const [hours, minutes] = time.split(':').map(Number);

      let hour24 = hours;
      if (period === 'PM' && hours !== 12) hour24 += 12;
      if (period === 'AM' && hours === 12) hour24 = 0;

      const endTime = new Date(now);
      endTime.setHours(hour24, minutes, 0, 0);

      // If it's currently before the end time hour on the same day, it's not past
      // This is a simple check - in reality we'd need the session date
      return now > endTime;
    } catch {
      return false;
    }
  };

  // Determine if livestream should be shown as live
  // For external links, use session time. For embeds, use API status
  const isLivestreamLive = livestreamData?.streamType === 'Link'
    ? isSessionCurrentlyLive()
    : livestreamData?.isLive || false;

  // Debug logging
  if (livestreamData) {
    console.log('Livestream data:', {
      streamType: livestreamData.streamType,
      isPast: livestreamData.isPast,
      isLive: livestreamData.isLive,
      isUpcoming: livestreamData.isUpcoming,
      showAfterEndsAt: livestreamData.showAfterEndsAt,
      sessionTime: session.time,
      sessionEndTime: session.endTime,
      isSessionCurrentlyLive: isSessionCurrentlyLive(),
      calculatedIsLivestreamLive: isLivestreamLive
    });
  }

  // Helper function to get file icon based on type
  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf':
        return <FileText size={18} className="text-red-500" />;
      case 'ppt':
        return <FileType size={18} className="text-orange-500" />;
      case 'doc':
        return <FileText size={18} className="text-blue-500" />;
      case 'xls':
        return <FileText size={18} className="text-green-500" />;
      case 'zip':
        return <File size={18} className="text-purple-500" />;
      default:
        return <File size={18} className="text-gray-500" />;
    }
  };
  // Handle calendar dropdown
  const handleCalendarClick = () => {
    setShowCalendarDropdown(!showCalendarDropdown);
  };
  // Handle livestream button click based on stream type
  const handleLivestreamClick = () => {
    if (!livestreamData || !livestreamId) return;

    // Track the view
    livestreamTracking.trackView(
      {
        id: livestreamId,
        name: livestreamData.name,
        sessionName: livestreamData.sessionName
      },
      'session_modal'
    );

    // Check if we should show the livestream based on timing and settings
    const canViewLivestream = isLivestreamLive ||
      (livestreamData.isPast && livestreamData.showAfterEndsAt && livestreamData.isRecordingAvailable) ||
      (livestreamData.streamType === 'Link' && livestreamData.showAfterEndsAt);

    if (!canViewLivestream) {
      return; // Don't allow viewing if conditions aren't met
    }

    // Check if stream should use built-in player
    const useBuiltInPlayer = livestreamData.streamType === 'Embed' &&
      (livestreamData.streamService === 'Internal' ||
       livestreamData.streamService === 'Custom Internal' ||
       livestreamData.streamService === 'RTMP');

    if (livestreamData.streamType === 'Link' && livestreamData.streamConfig?.link) {
      // Track external link click
      livestreamTracking.trackExternalLinkClick(
        {
          id: livestreamId,
          link: livestreamData.streamConfig.link,
          streamType: livestreamData.streamType
        },
        'session_modal'
      );
      // External link - open in new tab
      window.open(livestreamData.streamConfig.link, '_blank');
    } else if (useBuiltInPlayer) {
      // Built-in viewer for Embed streams with Internal/Custom Internal/RTMP service
      navigate(`/accounts/events/${eventSlug}/livestream/${livestreamId}`);
    } else if (livestreamData.streamConfig?.embedUrl) {
      // Track external embed click
      livestreamTracking.trackExternalLinkClick(
        {
          id: livestreamId,
          link: livestreamData.streamConfig.embedUrl,
          streamType: 'Embed'
        },
        'session_modal_embed'
      );
      // Fallback to embed URL for other types
      window.location.href = livestreamData.streamConfig.embedUrl;
    }
  };

  // Handle specific calendar selection
  const handleAddToCalendar = (type: 'google' | 'outlook' | 'apple' | 'outlookcom' | 'office365' | 'yahoo') => {
    if (onCalendarAdd) {
      onCalendarAdd(type);
    } else {
      // IMPORTANT: Prefer eventDate prop (the actual day date) over session's placeholder date
      const actualSessionDate = eventDate || extractSessionDate(session);
      // Get timezone from event details if available
      const timeZone = eventDetails?.timeZone;
      const url = generateCalendarUrl(session, type as any, actualSessionDate, timeZone);
      if (type === 'apple') {
        // For Apple Calendar, we need to create a temporary link to download the .ics file
        const link = document.createElement('a');
        link.href = url;
        link.download = `${session.title.replace(/\s+/g, '-')}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // For Google and Outlook, open in a new tab
        window.open(url, '_blank');
      }
    }
    setShowCalendarDropdown(false);
  };
  // Handle speaker click
  const handleSpeakerClick = (speakerId: string) => {
    if (onSpeakerClick) {
      onSpeakerClick(speakerId);
    } else if (allSpeakers && SpeakerModal) {
      // If we have speaker data and a modal component, handle it internally
      const fullSpeaker = allSpeakers.find(s => String(s.id) === String(speakerId));
      if (fullSpeaker) {
        setSelectedSpeaker(fullSpeaker);
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCalendarDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'Esc') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);
  return (
    <>
      <ModalWrapper isOpen={true} onClose={onClose}>
      <div className="space-y-6">
        {/* Tabs */}
        {(attachments && attachments.length > 0) && <div className="flex border-b">
            <button onClick={() => setActiveTab('details')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              Session Details
            </button>
            {attachments && attachments.length > 0 && <button onClick={() => setActiveTab('attachments')} className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${activeTab === 'attachments' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                Materials ({attachments.length})
              </button>}
          </div>}
        {activeTab === 'details' && <>
            <div className="flex gap-4">
              {image && <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-gray-100">
                  <img src={image} alt={title} className="w-full h-full object-contain" />
                </div>}
              <div className="space-y-2 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {type && <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium capitalize">
                    {type}
                  </span>}
                  {isLivestream && <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                      <Tv size={14} />
                      LIVE
                    </span>}
                </div>
                <h2 className="text-xl font-semibold">{title}</h2>
                {session.categories && session.categories.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {session.categories.map(category => (
                      <CategoryBadge key={category.id} category={category} size="md" />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Livestream UI - moved to details tab */}
            {isLivestream && (livestreamUrl || livestreamId) && (
              livestreamLoading ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 ml-2">Loading livestream...</p>
                  </div>
                </div>
              ) : livestreamData ? (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isLivestreamLive ? (
                        <>
                          <div className="relative">
                            <Radio className="w-5 h-5 text-red-500 animate-pulse" />
                            <span className="absolute -top-1 -right-1 flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                            </span>
                          </div>
                          <span className="font-medium text-red-600">Live Now</span>
                        </>
                      ) : livestreamData && livestreamData.streamType === 'Link' ? (
                        // For Link streams, we calculate status ourselves since backend is unreliable
                        !isSessionCurrentlyLive() && session.endTime ? (
                          <>
                            <Tv className="w-5 h-5 text-gray-500" />
                            <span className="font-medium text-gray-700">Past Livestream</span>
                          </>
                        ) : (
                          <>
                            <Clock className="w-5 h-5 text-gray-500" />
                            <span className="font-medium text-gray-700">Upcoming Livestream</span>
                          </>
                        )
                      ) : livestreamData && (livestreamData.isPast || livestreamData.status === 'PAST') ? (
                        <>
                          <Tv className="w-5 h-5 text-gray-500" />
                          <span className="font-medium text-gray-700">Past Livestream</span>
                        </>
                      ) : livestreamData && (livestreamData.isUpcoming || livestreamData.status === 'UPCOMING') ? (
                        <>
                          <Clock className="w-5 h-5 text-gray-500" />
                          <span className="font-medium text-gray-700">Upcoming Livestream</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-5 h-5 text-gray-500" />
                          <span className="font-medium text-gray-700">Livestream</span>
                        </>
                      )}
                    </div>

                    {(isLivestreamLive ||
                      (livestreamData?.isPast && livestreamData?.showAfterEndsAt && livestreamData?.isRecordingAvailable) ||
                      (livestreamData?.streamType === 'Link' && livestreamData?.showAfterEndsAt)) && (
                      <button
                        onClick={handleLivestreamClick}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                      >
                        {livestreamData?.streamType === 'Link' && <ExternalLink size={16} />}
                        {livestreamData?.streamType !== 'Link' && <Tv size={16} />}
                        {isLivestreamLive
                          ? 'Watch Livestream'
                          : livestreamData?.streamType === 'Link'
                          ? 'Access Livestream'
                          : 'Watch Recording'}
                      </button>
                    )}
                  </div>

                  <p className="text-sm text-gray-600">
                    {isLivestreamLive
                      ? 'This session is streaming live right now. Join to watch in real-time.'
                      : livestreamData?.streamType === 'Link'
                      ? !isSessionCurrentlyLive() && livestreamData?.showAfterEndsAt
                        ? 'This session has ended. You can still access the livestream link.'
                        : session.time
                        ? `This session will be streamed live at ${session.time}.`
                        : 'This livestream will start soon.'
                      : livestreamData?.isPast
                      ? livestreamData?.showAfterEndsAt && livestreamData?.isRecordingAvailable
                        ? 'This session has ended. You can watch the recording.'
                        : 'This livestream has ended.'
                      : livestreamData?.livestreamableType === 'Session' && session.time
                      ? `This session will be streamed live at ${session.time}.`
                      : 'This livestream will start soon.'}
                  </p>
                </div>
              ) : livestreamUrl ? (
                // Fallback for sessions with only livestreamUrl (no livestreamId)
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tv className="w-5 h-5 text-red-500" />
                      <span className="font-medium text-red-600">Livestream Available</span>
                    </div>

                    <a
                      href={livestreamUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      <ExternalLink size={16} />
                      Join External Livestream
                    </a>
                  </div>

                  <p className="text-sm text-gray-600">
                    This session has a livestream available. Click the button to join.
                  </p>
                </div>
              ) : null
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Clock size={18} />
                <div>
                  <span>
                    {time} - {endTime}
                  </span>
                  {timeZoneLabel && (
                    <div className="text-xs text-gray-500">{timeZoneLabel}</div>
                  )}
                </div>
              </div>
              {room && <div className="flex items-center gap-2 text-gray-600">
                  <MapPin size={18} />
                  <span>{room}</span>
                </div>}
            </div>
            {description && (
              <div className="space-y-2">
                <h3 className="font-medium">Description</h3>
                <div
                  className="tinymce-content"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </div>
            )}
            {speakers.length > 0 && (
              <div className="space-y-3">
                {groupSpeakersByType(speakers).map((group) => (
                  <div key={group.type} className="space-y-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <Users className="w-5 h-5 text-gray-400" />
                      {getSpeakerTypeLabel(group.type)}
                    </h3>
                    <div className="space-y-3">
                      {group.speakers.map((speaker: any) => (
                        <div
                          key={speaker.id}
                          className={`flex ${speaker.title ? 'items-start' : 'items-center'} gap-3 p-2 -m-2 rounded-lg transition-colors ${
                            onSpeakerClick || (allSpeakers && SpeakerModal) ? 'cursor-pointer hover:bg-gray-50' : ''
                          }`}
                          onClick={() => (onSpeakerClick || (allSpeakers && SpeakerModal)) && handleSpeakerClick(speaker.id)}
                          title={(onSpeakerClick || (allSpeakers && SpeakerModal)) ? 'Click to view speaker details' : ''}
                        >
                          <div className="relative w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                            {speaker.image ? (
                              <>
                                {/* Blurred background */}
                                <div className="absolute inset-0">
                                  <img
                                    src={speaker.image}
                                    alt=""
                                    className="w-full h-full object-cover blur-xl scale-110 opacity-50"
                                    aria-hidden="true"
                                  />
                                </div>
                                {/* Main image */}
                                <div className="relative h-full w-full flex items-center justify-center">
                                  <img
                                    src={speaker.image}
                                    alt={speaker.name}
                                    className="w-full h-full object-contain"
                                    onError={(e: any) => {
                                      e.currentTarget.src = 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200&h=200';
                                    }}
                                  />
                                </div>
                              </>
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-600">
                                <Users size={20} />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium hover:text-blue-600 transition-colors">{speaker.name}</p>
                            {(speaker.title || speaker.company) && (
                              <p className="text-sm text-gray-600">
                                {[speaker.title, speaker.company].filter(Boolean).join(' at ')}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Session Sponsors */}
            {sponsors && sponsors.length > 0 && <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <HeartHandshake className="w-5 h-5 text-gray-400" />
                  Session Sponsors
                </h3>
                <div className="flex flex-wrap gap-3">
                  {sponsors.map((sponsor: any) => <a key={sponsor.id} href={sponsor.website} target="_blank" rel="noopener noreferrer" className="bg-gray-50 border rounded-lg p-3 hover:bg-gray-100 transition-colors">
                      <img src={sponsor.logo} alt={sponsor.name} className="h-8 max-w-[120px] object-contain" />
                    </a>)}
                </div>
              </div>}
            {capacity > 0 && <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Available Seats</span>
                  <span className="font-medium">
                    {capacity - (registeredCount || 0)} / {capacity}
                  </span>
                </div>
                <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 rounded-full" style={{
              width: `${(registeredCount || 0) / capacity * 100}%`
            }} />
                </div>
              </div>}
          </>}
        {activeTab === 'attachments' && attachments && <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                Materials
              </h3>
            </div>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {attachments.map((attachment: any) => <a
                  key={attachment.id}
                  href={attachment.url}
                  download={attachment.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {getFileIcon(attachment.type)}
                    <div>
                      <p className="font-medium">{attachment.name}</p>
                      <p className="text-xs text-gray-500">
                        {attachment.type.toUpperCase()} • {attachment.size}
                      </p>
                    </div>
                  </div>
                  <div className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full">
                    <Download size={18} />
                  </div>
                </a>)}
            </div>
          </div>}
        <div className="flex justify-between gap-3 border-t pt-4 mt-4">
          <div>
            {survey && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (survey.url && !survey.isCompleted) {
                    window.open(survey.url, '_blank', 'noopener,noreferrer');
                  }
                }}
                className={`
                  inline-flex items-center rounded-full font-medium transition-all
                  text-sm px-3 py-1.5 gap-1.5
                  relative overflow-hidden group
                  ${
                    survey.isCompleted
                      ? 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-200'
                      : 'text-white hover:shadow-md border'
                  }
                `}
                style={{
                  backgroundColor: survey.isCompleted ? undefined : `${primaryColor}20`,
                  color: survey.isCompleted ? undefined : primaryColor,
                  borderColor: survey.isCompleted ? undefined : `${primaryColor}40`
                }}
                onMouseEnter={(e) => {
                  if (!survey.isCompleted) {
                    e.currentTarget.style.backgroundColor = `${primaryColor}30`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!survey.isCompleted) {
                    e.currentTarget.style.backgroundColor = `${primaryColor}20`;
                  }
                }}
                disabled={survey.isCompleted}
              >
                {/* Pulsing background effect when not completed */}
                {!survey.isCompleted && (
                  <span
                    className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                    style={{ backgroundColor: primaryColor }}
                  ></span>
                )}

                {/* Icon */}
                {survey.isCompleted ? (
                  <CheckCircle size={16} className="flex-shrink-0" />
                ) : (
                  <MessageSquare size={16} className="flex-shrink-0" />
                )}

                {/* Text */}
                <span>{survey.isCompleted ? 'Survey Completed' : 'Take Survey'}</span>

                {/* External link icon */}
                {!survey.isCompleted && survey.url && (
                  <ExternalLink size={12} className="ml-0.5" />
                )}
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
              Close
            </button>
            {/* Calendar Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={handleCalendarClick}
                className="px-4 py-2 text-white rounded-lg flex items-center gap-2 transition-all"
                style={{
                  backgroundColor: primaryColor,
                  filter: 'brightness(1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
              >
                <Calendar size={16} />
                Add to Calendar
                {showCalendarDropdown ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showCalendarDropdown && (
                <div className="absolute right-0 bottom-full mb-2 bg-white border rounded-lg shadow-lg py-1 z-50 min-w-[200px]">
                  <button
                    onClick={() => handleAddToCalendar('apple')}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                  >
                    {eventDetails?.calendarIcons?.apple ? (
                      <img src={eventDetails.calendarIcons.apple} alt="Apple" className="w-4 h-4" />
                    ) : (
                      <span className="w-4 h-4 text-gray-400">🍎</span>
                    )}
                    Apple
                  </button>
                  <button
                    onClick={() => handleAddToCalendar('google')}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                  >
                    {eventDetails?.calendarIcons?.google ? (
                      <img src={eventDetails.calendarIcons.google} alt="Google" className="w-4 h-4" />
                    ) : (
                      <span className="w-4 h-4 text-gray-400">📅</span>
                    )}
                    Google <span className="text-gray-400 text-xs">(online)</span>
                  </button>
                  <button
                    onClick={() => handleAddToCalendar('office365')}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                  >
                    {eventDetails?.calendarIcons?.office365 ? (
                      <img src={eventDetails.calendarIcons.office365} alt="Office 365" className="w-4 h-4" />
                    ) : (
                      <span className="w-4 h-4 text-gray-400">📊</span>
                    )}
                    Office 365 <span className="text-gray-400 text-xs">(online)</span>
                  </button>
                  <button
                    onClick={() => handleAddToCalendar('outlook')}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                  >
                    {eventDetails?.calendarIcons?.outlook ? (
                      <img src={eventDetails.calendarIcons.outlook} alt="Outlook" className="w-4 h-4" />
                    ) : (
                      <span className="w-4 h-4 text-gray-400">📧</span>
                    )}
                    Outlook
                  </button>
                  <button
                    onClick={() => handleAddToCalendar('outlookcom')}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                  >
                    {eventDetails?.calendarIcons?.outlookcom ? (
                      <img src={eventDetails.calendarIcons.outlookcom} alt="Outlook.com" className="w-4 h-4" />
                    ) : (
                      <span className="w-4 h-4 text-gray-400">📧</span>
                    )}
                    Outlook.com <span className="text-gray-400 text-xs">(online)</span>
                  </button>
                  <button
                    onClick={() => handleAddToCalendar('yahoo')}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3"
                  >
                    {eventDetails?.calendarIcons?.yahoo ? (
                      <img src={eventDetails.calendarIcons.yahoo} alt="Yahoo" className="w-4 h-4" />
                    ) : (
                      <span className="w-4 h-4 text-gray-400">📮</span>
                    )}
                    Yahoo <span className="text-gray-400 text-xs">(online)</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </ModalWrapper>

      {/* Speaker Modal */}
      {selectedSpeaker && SpeakerModal && (
        <SpeakerModal
          speaker={selectedSpeaker}
          onClose={() => setSelectedSpeaker(null)}
        />
      )}
    </>
  );
};